import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { wireToTriageRows } from "../../api";
import { useUploadStatus } from "../../hooks/use-upload-status";
import { isUploadBusy, startCreateBatch, startPricingImport } from "../../state/upload-status";
import { UploadMenu, type UploadMenuAction } from "../../components/upload-menu";
import { OrionSidebar } from "../../components/orion-sidebar";
import { PaginationFooter } from "../../components/pagination-footer";
import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { useSubmissions } from "../../hooks/use-submission";
import { useSavedViews } from "../../hooks/use-saved-views";
import { ActiveFiltersBar } from "./active-filters-bar";
import { CustomizeDrawer } from "./customize-drawer";
import { EMPTY_FILTERS, FiltersDrawer, countActiveFilters, filtersToParams, type TriageFilters } from "./filters-drawer";
import { FeaturedAlert } from "../../components/featured-alert";
import { FloatingAlert } from "../../components/floating-alert";
import { ThreadResolvedToast } from "../../components/comments/thread-resolved-toast";
import { TriageEmptyState, TriageErrorState, TriageOfflineState } from "./states";
import { SubmissionsTable } from "./submissions-table";
import { TriageToolbar } from "./toolbar";
import { type TriageView, allColumnIds, defaultVisibleIds } from "./triage-columns";
import { SaveViewModal } from "./save-view-modal";
import { SavedViewsMenu } from "./saved-views-menu";
import { useTriageComments } from "./use-triage-comments";
import { type LastLayout, type SavedView, mergeFilters, newSavedView } from "./view-preference-config";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Layout shape (no breadcrumb):
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Sidebar │ Page header (title + CTA)  ← non-scrolling          │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Toolbar (tabs · filters · search) ← non-scrolling   │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Table headers ← sticky                               │
 *   │         │ ┌──────────── scroll area ─────────────────────────┐ │
 *   │         │ │ rows                                             │ │
 *   │         │ └──────────────────────────────────────────────────┘ │
 *   │         ├──────────────────────────────────────────────────── │
 *   │         │ Pagination footer ← non-scrolling                    │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The card container does not scroll; the body of the table does.
 */
export function SubmissionsListPage() {
    const [offset, setOffset] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [search, setSearch] = useState("");
    const [view, setView] = useState<TriageView>("submissions");
    const [customizeOpen, setCustomizeOpen] = useState(false);
    /** Visible column ids per view (Customize). Seeded from each view's
     *  default simplified set, then replaced by the user's saved layout
     *  once it loads (OR-1384). */
    const [visibleColumnIds, setVisibleColumnIds] = useState<Record<TriageView, string[]>>(() => ({
        submissions: defaultVisibleIds("submissions"),
        facilities: defaultVisibleIds("facilities"),
    }));
    /** Column order per view (Customize drag-to-reorder). Seeded from each
     *  view's natural column order. */
    const [columnOrder, setColumnOrder] = useState<Record<TriageView, string[]>>(() => ({
        submissions: allColumnIds("submissions"),
        facilities: allColumnIds("facilities"),
    }));
    /** Sort directive in FastAPI/DRF format: `field` asc, `-field` desc.
     *  Lifted to the page so paging + sorting share a single fetch. */
    const [sort, setSort] = useState<string | undefined>(undefined);
    const [filtersOpen, setFiltersOpen] = useState(false);
    /** Committed filters — written by the drawer's Show results /
     *  Clear All actions, never on individual control changes. */
    const [filters, setFilters] = useState<TriageFilters>(EMPTY_FILTERS);

    // ── Saved Views (OR-1384, design OR-1507) ─────────────────────
    // Named snapshots of filters + columns the underwriter can re-apply
    // instead of rebuilding the same filter set every morning. Stored on
    // their user profile (backend OR-1385) and scoped per tab, since the
    // two tabs have different column sets.
    const savedViews = useSavedViews();
    const viewsReady = savedViews.status === "ready";
    const viewsForTab = savedViews.views[view];
    /** Ids applied right now. The design allows combining several, and
     *  combining them with ad-hoc filters. Runtime only — it is not
     *  persisted, because the design never shows an applied view being
     *  restored on load. */
    const [appliedViewIds, setAppliedViewIds] = useState<string[]>([]);
    /** Guards the hydration effect so it seeds the page exactly once; after
     *  that the user's own edits own the state. */
    const hydrating = useRef(false);
    /** Mirrors it as state so the list request can wait: the restored
     *  filters are query params, and firing before they land would fetch an
     *  unfiltered page and flash it before refetching. */
    const [hydrated, setHydrated] = useState(false);
    const [saveViewOpen, setSaveViewOpen] = useState(false);
    /** Whether this tab accepts edits at all: the layout has to be restored
     *  first, and a tab whose preferences could not be read stays read-only
     *  for the session rather than dropping writes without saying so. */
    const canEdit = hydrated && savedViews.canWrite[view];
    const [viewsError, setViewsError] = useState<string | null>(null);

    // OR-1384: restore the layout each tab was last left in. No UI of its
    // own — the table simply comes back the way the user had it.
    //
    // Runs exactly once, not per tab. Column state is keyed by tab so both
    // can be seeded here, but `filters` and `appliedViewIds` are a single
    // state shared across the two tabs (OR-979 kept them shared). Seeding
    // those again on a tab switch would push the other tab's filters onto
    // the table the user is looking at, so only the landing tab seeds them.
    useEffect(() => {
        if (!viewsReady || hydrating.current) return;
        hydrating.current = true;
        const { submissions, facilities } = savedViews.lastLayout;
        setVisibleColumnIds((prev) => ({
            submissions: submissions?.visibleColumnIds ?? prev.submissions,
            facilities: facilities?.visibleColumnIds ?? prev.facilities,
        }));
        setColumnOrder((prev) => ({
            submissions: submissions?.columnOrder ?? prev.submissions,
            facilities: facilities?.columnOrder ?? prev.facilities,
        }));
        const landing = savedViews.lastLayout[view];
        if (landing) {
            setFilters(landing.filters);
            // Drop ids whose view has since been deleted.
            const alive = new Set(savedViews.views[view].map((v) => v.id));
            setAppliedViewIds(landing.appliedViewIds.filter((id) => alive.has(id)));
        }
        setHydrated(true);
    }, [viewsReady, view, savedViews.lastLayout, savedViews.views]);

    const appliedViews = appliedViewIds
        .map((id) => viewsForTab.find((v) => v.id === id))
        .filter((v): v is SavedView => v != null);

    // A saved view contributes its filters; the ad-hoc set goes last so a
    // filter the user just set beats one that arrived through a view.
    const effectiveFilters = mergeFilters([...appliedViews.map((v) => v.filters), filters]);

    /** Record the tab's resulting layout after any commit. Callers pass
     *  only what they changed; the rest is read from current state. */
    const rememberLayout = useCallback(
        (patch: Partial<LastLayout> = {}) => {
            savedViews.rememberLayout(view, {
                visibleColumnIds: visibleColumnIds[view],
                columnOrder: columnOrder[view],
                filters,
                appliedViewIds,
                ...patch,
            });
        },
        [savedViews, view, visibleColumnIds, columnOrder, filters, appliedViewIds],
    );

    /** Applying a view writes its column layout into the tab's state, so
     *  Customize keeps behaving normally on top of it. Removing the view
     *  later drops its filters but leaves the columns where they are —
     *  the design shows no revert. */
    // The next value is computed here rather than inside a setState
    // updater: React may run an updater more than once or throw the render
    // away, and these schedule a preference write, which must happen once.
    const toggleSavedView = useCallback(
        (id: string) => {
            setOffset(0);
            if (appliedViewIds.includes(id)) {
                const next = appliedViewIds.filter((x) => x !== id);
                setAppliedViewIds(next);
                rememberLayout({ appliedViewIds: next });
                return;
            }
            const next = [...appliedViewIds, id];
            setAppliedViewIds(next);
            const target = viewsForTab.find((v) => v.id === id);
            if (!target) {
                rememberLayout({ appliedViewIds: next });
                return;
            }
            setVisibleColumnIds((prev) => ({ ...prev, [view]: target.visibleColumnIds }));
            setColumnOrder((prev) => ({ ...prev, [view]: target.columnOrder }));
            rememberLayout({
                appliedViewIds: next,
                visibleColumnIds: target.visibleColumnIds,
                columnOrder: target.columnOrder,
            });
        },
        [appliedViewIds, viewsForTab, view, rememberLayout],
    );

    const removeSavedView = useCallback(
        (id: string) => {
            setOffset(0);
            const next = appliedViewIds.filter((x) => x !== id);
            setAppliedViewIds(next);
            rememberLayout({ appliedViewIds: next });
        },
        [appliedViewIds, rememberLayout],
    );

    const deleteSavedView = useCallback(
        (id: string) => {
            const next = appliedViewIds.filter((x) => x !== id);
            setAppliedViewIds(next);
            rememberLayout({ appliedViewIds: next });
            void savedViews.replace(view, viewsForTab.filter((v) => v.id !== id)).then((ok) => {
                if (!ok) setViewsError("Couldn't delete Saved View");
            });
        },
        [appliedViewIds, savedViews, view, viewsForTab, rememberLayout],
    );

    /** "Save current view" captures the layout as the user sees it —
     *  the merged filters, so a view built on top of another view keeps
     *  working standalone. */
    const saveCurrentView = useCallback(
        (name: string) => {
            const created = newSavedView(name, {
                visibleColumnIds: visibleColumnIds[view],
                columnOrder: columnOrder[view],
                filters: effectiveFilters,
            });
            void savedViews.replace(view, [...viewsForTab, created]).then((ok) => {
                if (ok) {
                    // Saving applies it, so the toolbar names the view the
                    // user just created instead of staying generic.
                    setAppliedViewIds([created.id]);
                    setFilters(EMPTY_FILTERS);
                    rememberLayout({ appliedViewIds: [created.id], filters: EMPTY_FILTERS });
                } else {
                    setViewsError("Couldn't save Saved View");
                }
            });
        },
        [savedViews, view, viewsForTab, visibleColumnIds, columnOrder, effectiveFilters, rememberLayout],
    );

    const commitFilters = useCallback(
        (next: TriageFilters) => {
            setOffset(0);
            setFilters(next);
            rememberLayout({ filters: next });
        },
        [rememberLayout],
    );

    /** Clear All drops the ad-hoc filters and every applied view. */
    const clearAllFilters = useCallback(() => {
        setOffset(0);
        setFilters(EMPTY_FILTERS);
        setAppliedViewIds([]);
        rememberLayout({ filters: EMPTY_FILTERS, appliedViewIds: [] });
    }, [rememberLayout]);

    // Committed filters → real backend query params; the toolbar search
    // box drives the `search` slot (debounced so typing doesn't fire a
    // request per keystroke). Backend `search` is a substring match on the
    // submission's title and FNI only — in BOTH views (verified against the
    // repositories, 2026-09-07). Facility name, CCN, address and zip are NOT
    // searched yet; that is backend OR-1387, the other half of OR-1386.
    const debouncedSearch = useDebouncedValue(search.trim(), 300);
    // Held until the restored layout is in state: the filters below are
    // query params, so listing first would fetch an unfiltered page and
    // immediately refetch.
    const query = useSubmissions(
        {
            view: view === "facilities" ? "facility" : "submission",
            offset,
            limit: pageSize,
            search: debouncedSearch || undefined,
            sort,
            ...filtersToParams(effectiveFilters),
        },
        { enabled: hydrated },
    );
    const filterCount = countActiveFilters(effectiveFilters);
    const navigate = useNavigate();

    // Cell comments ("Notes", OR-1534).
    const triageComments = useTriageComments(query.status === "ready" ? query.data : null);

    // ── File-upload shortcut ──────────────────────────────────────
    // A provisional CTA next to "Create a Submission": opens the OS
    // file picker, POSTs the selected files to
    // `/v2/submissions/from-upload`, and jumps to the new submission's
    // UW Information page on success. No bespoke upload UI by design
    // — the user explicitly asked to lean on the native dialog.
    /** One picker per creation path, because the accepted formats differ:
     *  broker documents are what the extractor reads, the pricing model is
     *  a workbook. Both hand off to the global upload store, so the panel
     *  they raise survives navigating away from this page. */
    const brokerInputRef = useRef<HTMLInputElement>(null);
    const pricingInputRef = useRef<HTMLInputElement>(null);
    const uploadStatus = useUploadStatus();
    // One upload at a time across the product — this button and the ones
    // inside a submission all gate on the same store.
    const uploadInProgress = isUploadBusy(uploadStatus);

    const onCreateAction = useCallback((action: UploadMenuAction) => {
        if (action === "blank") {
            navigate("/submissions/new");
        } else if (action === "broker-documents") {
            brokerInputRef.current?.click();
        } else if (action === "pricing-model") {
            pricingInputRef.current?.click();
        }
    }, [navigate]);

    const onBrokerFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        // Clear first so re-picking the same file still fires `onChange`.
        event.target.value = "";
        if (files.length === 0) return;
        // Fire and forget: the store owns the job from here, and the
        // floating panel reports it wherever the user goes next.
        startCreateBatch(files);
    };

    const onPricingFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        // The floating panel owns it from here: one row for the workbook,
        // progress, Failed + Try again — and since the job builds a NEW
        // submission whose id only exists at completion, the panel's CTA
        // becomes View Submission once it lands.
        if (!startPricingImport(file)) {
            window.alert("Another upload is already in progress. Wait for it to finish and try again.");
        }
    };

    const total = query.status === "ready" ? query.data.total : 0;
    const rows = query.status === "ready" ? wireToTriageRows(query.data.items, query.data.view) : [];

    const isOffline = query.status === "error" && typeof navigator !== "undefined" && !navigator.onLine;
    const bodyState: "error" | "offline" | "no-results" | "data-or-loading" = isOffline
        ? "offline"
        : query.status === "error"
          ? "error"
          : query.status === "ready" && rows.length === 0
            ? "no-results"
            : "data-or-loading";

    return (
        <div className="bg-secondary flex min-h-screen flex-col lg:h-screen lg:flex-row">
            {/* SidebarNavigationSlim handles its own responsive split:
             *  fixed rail at lg+, full-width mobile header below lg
             *  (with a hamburger that opens the nav drawer). */}
            <OrionSidebar />

            <div className="flex flex-1 flex-col overflow-hidden">
                <header className="flex items-start justify-between gap-3 px-4 pb-4 pt-4 md:px-6 md:pb-6 md:pt-6">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-primary text-xl font-semibold">Submission Triage</h1>
                        <p className="text-md text-secondary">Manage all underwriting submissions</p>
                    </div>
                    {/* CTA — split into two breakpoint-conditional Buttons so
                      * the mobile variant (no children, just the icon) hits
                      * atlas-ui's auto icon-only mode, while the desktop
                      * variant keeps the regular text + icon look.
                      *
                      * On mobile we bump to `size="lg"` (44px square) and
                      * override the corner radius from atlas-ui's default
                      * `rounded-lg` (8px) to `rounded-2xl` (16px). Atlas's
                      * default reads near-circular at small sizes; the
                      * Figma mock shows distinctly chunky-square corners,
                      * so we lift both size AND radius. The inner gradient
                      * ring (`before:rounded-[7px]` baked into the size-lg
                      * style) gets re-anchored to `15px` so it stays
                      * concentric with the new outer radius (1px inset for
                      * the 1px ring matches atlas's existing math).
                      *
                      * Icon override pushes the `+` from atlas-ui's default
                      * `text-white/60` to fully opaque so the glyph reads
                      * cleanly on the brand-solid background. */}
                    {/* Group Upload + Create together so the header's
                      * `justify-between` only sees [title] vs [CTAs]
                      * — otherwise the two buttons would spread to
                      * opposite ends of the right-side area. The inner
                      * `gap-3` keeps them adjacent with the same
                      * breathing room the header already uses. */}
                    {/* One menu button replaces the old Upload + Create pair
                      * (Import/Export Flow V2): one primary action opening the
                      * three creation paths. Disabled while any upload is in
                      * flight, here or inside a submission. */}
                    <UploadMenu
                        variant="triage"
                        onAction={onCreateAction}
                        isDisabled={uploadInProgress}
                        data-autotest="triage-header-button-create"
                    />
                    {/* Broker documents: the formats the extractor reads. */}
                    <input
                        ref={brokerInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                        onChange={onBrokerFilesSelected}
                        className="hidden"
                        aria-hidden="true"
                    />
                    {/* Pricing model: one workbook. */}
                    <input
                        ref={pricingInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => void onPricingFileSelected(e)}
                        className="hidden"
                        aria-hidden="true"
                    />
                </header>

                <section className="relative flex min-h-0 flex-1 px-0 pb-0 sm:px-6 sm:pb-6">
                    {viewsError && (
                        <FloatingAlert>
                            <FeaturedAlert
                                color="error"
                                className="shadow-lg"
                                title={viewsError}
                                description="Please try again"
                                onClose={() => setViewsError(null)}
                                data-autotest="triage-saved-views-alert-error"
                            />
                        </FloatingAlert>
                    )}
                    {(triageComments.resolved || triageComments.undoError) && (
                        <FloatingAlert>
                            {triageComments.resolved && (
                                <ThreadResolvedToast
                                    onUndo={() => void triageComments.undo()}
                                    onClose={triageComments.dismissResolved}
                                    isUndoing={triageComments.undoing}
                                />
                            )}
                            {triageComments.undoError && (
                                <FeaturedAlert
                                    color="error"
                                    className="shadow-lg"
                                    title={triageComments.undoError}
                                    onClose={triageComments.dismissUndoError}
                                    data-autotest="comment-thread-undo-alert-error"
                                />
                            )}
                        </FloatingAlert>
                    )}
                    {/* No max-width here: the Triage table should grow with the
                      * viewport so wide monitors don't leave a gap on the right
                      * (the columns stretch to fill — see SubmissionsTable).
                      * Detail/form pages keep `max-w-dashboard` for readability. */}
                    <div className="bg-primary border-secondary flex min-h-0 w-full flex-1 flex-col overflow-hidden border-t sm:rounded-xl sm:border sm:shadow-xs">
                        <TriageToolbar
                            view={view}
                            onViewChange={(next) => {
                                setOffset(0);
                                setSort(undefined);
                                setView(next);
                            }}
                            search={search}
                            onSearchChange={(value) => {
                                setOffset(0);
                                setSearch(value);
                            }}
                            filterCount={filterCount}
                            onOpenFilters={() => setFiltersOpen(true)}
                            onOpenCustomize={() => setCustomizeOpen(true)}
                            isDisabled={!canEdit}
                            savedViews={
                                <SavedViewsMenu
                                    isDisabled={!canEdit}
                                    views={viewsForTab}
                                    appliedIds={appliedViewIds}
                                    onToggle={toggleSavedView}
                                    onDelete={deleteSavedView}
                                    onSaveCurrent={() => setSaveViewOpen(true)}
                                />
                            }
                            savedViewsCompact={
                                <SavedViewsMenu
                                    compact
                                    isDisabled={!canEdit}
                                    views={viewsForTab}
                                    appliedIds={appliedViewIds}
                                    onToggle={toggleSavedView}
                                    onDelete={deleteSavedView}
                                    onSaveCurrent={() => setSaveViewOpen(true)}
                                />
                            }
                        />

                        <ActiveFiltersBar
                            filters={filters}
                            total={total}
                            onChange={commitFilters}
                            onClearAll={clearAllFilters}
                            appliedViews={appliedViews}
                            onRemoveView={removeSavedView}
                        />

                        {bodyState === "error" && <TriageErrorState onRetry={() => window.location.reload()} />}
                        {bodyState === "offline" && <TriageOfflineState onRefresh={() => window.location.reload()} />}
                        {bodyState === "no-results" && <TriageEmptyState />}
                        {bodyState === "data-or-loading" && (
                            <>
                                <SubmissionsTable
                                    rows={rows}
                                    loading={query.status === "loading"}
                                    sort={sort}
                                    onSortChange={(next) => {
                                        setOffset(0);
                                        setSort(next);
                                    }}
                                    view={view}
                                    visibleColumnIds={visibleColumnIds[view]}
                                    columnOrder={columnOrder[view]}
                                    comments={triageComments.comments}
                                />
                                <PaginationFooter
                                    total={total}
                                    offset={offset}
                                    limit={pageSize}
                                    onChange={setOffset}
                                    onLimitChange={setPageSize}
                                    label="submissions"
                                    isDisabled={query.status === "loading"}
                                />
                            </>
                        )}
                    </div>
                </section>
            </div>

            <FiltersDrawer
                isOpen={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={filters}
                matchCount={total}
                onApply={commitFilters}
                onClearAll={clearAllFilters}
            />

            <SaveViewModal isOpen={saveViewOpen} onOpenChange={setSaveViewOpen} onSave={saveCurrentView} />

            <CustomizeDrawer
                isOpen={customizeOpen}
                onOpenChange={setCustomizeOpen}
                view={view}
                value={visibleColumnIds[view]}
                order={columnOrder[view]}
                onApply={(next) => {
                    setVisibleColumnIds((prev) => ({ ...prev, [view]: next.visibleIds }));
                    setColumnOrder((prev) => ({ ...prev, [view]: next.order }));
                    rememberLayout({ visibleColumnIds: next.visibleIds, columnOrder: next.order });
                }}
            />
        </div>
    );
}
